import React from 'react';
import { Button } from './ui/button';
import { FileText, Download, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from './Navbar';
import Footer from './Footer';

const DocsPage = () => {
  const { language } = useLanguage();
  
  const documents = [
    {
      id: 'en',
      title: 'Heliophant Market Analysis Project Description',
      language: 'English',
      languageCode: 'EN',
      description: 'Complete project overview, methodology, and technical specifications in English.',
      url: 'https://customer-assets.emergentagent.com/job_a726bb1d-ce91-4462-bf0b-b17c01bdef9e/artifacts/obzjdoe2_Helio_Market_Pulse_Project_Description_EN.pdf',
      icon: '🇬🇧',
      size: 'PDF Document',
      category: 'Project Documentation'
    },
    {
      id: 'gr',
      title: 'Περιγραφή Έργου Heliophant Market Analysis',
      language: 'Ελληνικά',
      languageCode: 'GR',
      description: 'Πλήρης περιγραφή του έργου, μεθοδολογία και τεχνικές προδιαγραφές στα Ελληνικά.',
      url: 'https://customer-assets.emergentagent.com/job_heliophant-algo/artifacts/ftmuwih1_Helio_Market_Pulse_Project_Description_GR.pdf',
      icon: '🇬🇷',
      size: 'PDF Έγγραφο',
      category: 'Project Documentation'
    }
  ];

  const sampleReports = [
    {
      id: 'sample-en',
      title: 'Weekly Quantitative Market Analysis',
      language: 'English',
      languageCode: 'EN',
      description: 'Sample Research Reports - Weekly quantitative analysis of market trends and predictions.',
      url: 'https://customer-assets.emergentagent.com/job_heliophant-algo/artifacts/y5x0nt10_Heliophant_Weekly_Quantitative_Market_Analysis_EN.pdf',
      icon: '🇬🇧',
      size: 'PDF Document'
    },
    {
      id: 'sample-gr',
      title: 'Εβδομαδιαία Ποσοτική Ανάλυση Αγοράς',
      language: 'Ελληνικά',
      languageCode: 'GR',
      description: 'Δείγματα Αναφορών Έρευνας - Εβδομαδιαία ποσοτική ανάλυση τάσεων και προβλέψεων αγοράς.',
      url: 'https://customer-assets.emergentagent.com/job_heliophant-algo/artifacts/ho28hsp2_%CE%95%CE%B2%CE%B4%CE%BF%CE%BC%CE%B1%CE%B4%CE%B9%CE%B1%CE%AF%CE%B1%20%CE%A0%CE%BF%CF%83%CE%BF%CF%84%CE%B9%CE%BA%CE%AE%20%CE%91%CE%BD%CE%AC%CE%BB%CF%85%CF%83%CE%B7%20%CE%91%CE%B3%CE%BF%CF%81%CF%8E%CE%BD%20%E2%80%93%20GR.pdf',
      icon: '🇬🇷',
      size: 'PDF Έγγραφο'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050811]">
      <Navbar />
      <div className="bg-gradient-to-b from-[#050811] via-[#0a0e27] to-[#050811] pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
              <span className="text-cyan-400 text-sm font-semibold">
                {language === 'el' ? 'Τεκμηρίωση' : 'Documentation'}
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {language === 'el' ? 'Τεκμηρίωση Έργου' : 'Project Documentation'}
            </h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              {language === 'el' 
                ? 'Κατεβάστε την πλήρη τεκμηρίωση του έργου Heliophant Market Analysis σε Αγγλικά και Ελληνικά.'
                : 'Download complete Heliophant Market Analysis project documentation in English and Greek.'}
            </p>
          </div>

          {/* Documents Grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-cyan-500/40 transition-all duration-300 group"
              >
                {/* Document Icon & Language Badge */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-14 h-14 bg-cyan-500/20 rounded-xl flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                      <FileText className="text-cyan-400" size={28} />
                    </div>
                    <span className="text-4xl">{doc.icon}</span>
                  </div>
                  <div className="px-3 py-1 bg-white/5 border border-white/20 rounded-full">
                    <span className="text-white/80 text-sm font-semibold">{doc.languageCode}</span>
                  </div>
                </div>

                {/* Document Info */}
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                  {doc.title}
                </h3>
                
                <div className="flex items-center space-x-2 mb-4">
                  <Globe size={16} className="text-white/60" />
                  <span className="text-white/60 text-sm">{doc.language}</span>
                </div>

                <p className="text-white/70 mb-6 leading-relaxed">
                  {doc.description}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <span className="text-white/60 text-sm">{doc.size}</span>
                  <Button
                    asChild
                    className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white group"
                  >
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      <Download size={18} className="mr-2" />
                      {language === 'el' ? 'Λήψη' : 'Download'}
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Sample Research Reports Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full mb-6">
                <span className="text-purple-400 text-sm font-semibold">
                  Sample Research Reports
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {language === 'el' ? 'Δείγματα Αναφορών Έρευνας' : 'Sample Research Reports'}
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                {language === 'el' 
                  ? 'Δείγματα από τις εβδομαδιαίες ποσοτικές αναλύσεις αγοράς μας.'
                  : 'Samples from our weekly quantitative market analysis reports.'}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {sampleReports.map((doc) => (
                <div
                  key={doc.id}
                  className="bg-gradient-to-br from-purple-500/5 to-white/[0.02] backdrop-blur-sm border border-purple-500/20 rounded-2xl p-8 hover:border-purple-500/40 transition-all duration-300 group"
                >
                  {/* Document Icon & Language Badge */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                        <FileText className="text-purple-400" size={28} />
                      </div>
                      <span className="text-4xl">{doc.icon}</span>
                    </div>
                    <div className="px-3 py-1 bg-white/5 border border-purple-500/30 rounded-full">
                      <span className="text-white/80 text-sm font-semibold">{doc.languageCode}</span>
                    </div>
                  </div>

                  {/* Document Info */}
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-purple-400 transition-colors">
                    {doc.title}
                  </h3>
                  
                  <div className="flex items-center space-x-2 mb-4">
                    <Globe size={16} className="text-white/60" />
                    <span className="text-white/60 text-sm">{doc.language}</span>
                  </div>

                  <p className="text-white/70 mb-6 leading-relaxed">
                    {doc.description}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <span className="text-white/60 text-sm">{doc.size}</span>
                    <Button
                      asChild
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white group"
                    >
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                      >
                        <Download size={18} className="mr-2" />
                        {language === 'el' ? 'Λήψη' : 'Download'}
                      </a>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-16 max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-blue-500/20 rounded-xl p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {language === 'el' ? 'Σχετικά με τα Έγγραφα' : 'About These Documents'}
                  </h3>
                  <p className="text-white/70 leading-relaxed">
                    {language === 'el'
                      ? 'Αυτά τα έγγραφα παρέχουν μια ολοκληρωμένη επισκόπηση του έργου Heliophant Market Analysis, συμπεριλαμβανομένης της μεθοδολογίας, της τεχνικής αρχιτεκτονικής και των στόχων του έργου. Είναι διαθέσιμα τόσο στα Αγγλικά όσο και στα Ελληνικά για την ευκολία σας.'
                      : 'These documents provide a comprehensive overview of the Heliophant Market Analysis project, including methodology, technical architecture, and project objectives. Available in both English and Greek for your convenience.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DocsPage;
